import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

interface Shop {
  id: number | string;
  name: string;
  image: string;
  url?: string;
}

interface Props {
  shops?: Shop[];
  totalPagesCount?: number;
  currentPage?: number | string;
  loading?: boolean;
  alphabetSort?: boolean;
}

type Alphabet = 'cyrillic' | 'latin' | 'other';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShopList',
  props: {
    shops: { default: () => [] },
    totalPagesCount: { default: undefined },
    currentPage: { default: undefined },
    loading: { type: Boolean },
    alphabetSort: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { shops: Shop[], totalPagesCount: number, currentPage: number | string, loading?: boolean, alphabetSort?: boolean };



const ALPHABET_TYPES_ORDER: Record<Alphabet, number> = {
  cyrillic: 1,
  latin: 2,
  other: 3,
};
const ALPHABET_LOCALES: Record<Alphabet, string | undefined> = {
  cyrillic: 'ru',
  latin: 'en',
  other: undefined, // Will be used first preferred language in browser settings (navigator.language)
};

const getAlphabetType = (str: string) => {
  // Normalize the string to a standard Unicode form (NFC) and remove leading/trailing whitespace
  const cleanedStr = str.normalize('NFC').trim();
  // Leave only letters
  const lettersOnly = cleanedStr.replace(/[^a-zа-яё]/gi, '');
  if (!lettersOnly) return 'other';

  // Check first leter
  if (/^[а-яё]/i.test(lettersOnly)) return 'cyrillic';
  if (/^[a-z]/i.test(lettersOnly)) return 'latin';
  return 'other';
};

const sortByAlphabetOrder = (shops: Shop[], alphabet: Alphabet) => {
  const locale = ALPHABET_LOCALES[alphabet];

  return shops.sort((a, b) => {
    try {
      return a.name.localeCompare(b.name, locale, { sensitivity: 'base' });
    } catch {
      // For WebView Android and Opera Android that does not support locales parameter and options parameter in localeCompare
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    }
  });
};

const splitByAlphabetType = (shops: Shop[]) => {
  return shops.reduce(
    (acc, shop) => {
      const alphabetType = getAlphabetType(shop.name);
      acc[alphabetType].push(shop);
      return acc;
    },
    {
      cyrillic: [] as Shop[],
      latin: [] as Shop[],
      other: [] as Shop[],
    },
  );
};

const joinByAlphabetTypesOrder = (
  sortedShopsByAlphabetType: Record<Alphabet, Shop[]>,
) => {
  const sortedKeys = Object.keys(ALPHABET_TYPES_ORDER).sort(
    (a, b) =>
      ALPHABET_TYPES_ORDER[a as Alphabet] - ALPHABET_TYPES_ORDER[b as Alphabet],
  );

  return sortedKeys.flatMap(
    (key) => sortedShopsByAlphabetType[key as Alphabet],
  );
};

const sortShopsByNames = (shops: Shop[]) => {
  const shopsByAlphabetType = splitByAlphabetType(shops);
  Object.keys(shopsByAlphabetType).forEach((type) =>
    sortByAlphabetOrder(
      shopsByAlphabetType[type as Alphabet],
      type as Alphabet,
    ),
  );

  return joinByAlphabetTypesOrder(shopsByAlphabetType);
};

const shopsList = computed(() => {
  return props.alphabetSort ? sortShopsByNames(props.shops) : props.shops;
});

return { __sfc: true,props, ALPHABET_TYPES_ORDER, ALPHABET_LOCALES, getAlphabetType, sortByAlphabetOrder, splitByAlphabetType, joinByAlphabetTypesOrder, sortShopsByNames, shopsList }
}

})